import CreateManifest from '@/components/manifest/CreateManifest';
import ManifestDetail from '@/components/manifest/ManifestDetail';
import ManifestList from '@/components/manifest/ManifestList';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import manifestService, { Manifest } from '@/services/manifestService';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';

type ViewMode = 'list' | 'create' | 'detail';

const ManifestManagement: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [selectedManifestId, setSelectedManifestId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [manifestToDelete, setManifestToDelete] = useState<Manifest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    manifestNo: '',
    search: '',
  });
  const { toast } = useToast();

  // Fetch manifests
  const fetchManifests = async () => {
    setLoading(true);
    try {
      const response = await manifestService.getManifests({
        page,
        limit: 10,
        status: filters.status || undefined,
        manifestNo: filters.manifestNo || undefined,
        search: filters.search || undefined,
      });

      setManifests(response.data);
      setTotal(response.total);
    } catch (error: any) {
      console.error('Failed to fetch manifests:', error);
      toast({
        title: 'Failed to fetch manifests',
        description: error.message || 'Unable to load manifests right now.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'list') {
      fetchManifests();
    }
  }, [viewMode, page, filters]);

  // Handle create success
  const handleCreateSuccess = (manifest: Manifest) => {
    toast({
      title: 'Manifest created',
      description: `Manifest ${manifest.manifestNo} created successfully.`,
    });
    setSelectedManifestId(manifest._id);
    setViewMode('detail');
  };

  // Handle view manifest
  const handleViewManifest = (manifest: Manifest) => {
    setSelectedManifestId(manifest._id);
    setViewMode('detail');
  };

  // Handle lock manifest
  const handleLockManifest = async (manifest: Manifest) => {
    try {
      await manifestService.lockManifest(manifest._id);
      toast({
        title: 'Manifest locked',
        description: `Manifest ${manifest.manifestNo} locked successfully.`,
      });
      fetchManifests();
    } catch (error: any) {
      toast({
        title: 'Failed to lock manifest',
        description: error.message || 'Unable to lock manifest right now.',
        variant: 'destructive',
      });
    }
  };

  // Handle delete manifest
  const handleDeleteManifest = (manifest: Manifest) => {
    setManifestToDelete(manifest);
  };

  const handleConfirmDeleteManifest = async () => {
    if (!manifestToDelete) return;
    setIsDeleting(true);

    try {
      await manifestService.deleteManifest(manifestToDelete._id);
      toast({
        title: 'Manifest deleted',
        description: `Manifest ${manifestToDelete.manifestNo} deleted successfully.`,
      });
      fetchManifests();
    } catch (error: any) {
      toast({
        title: 'Failed to delete manifest',
        description: error.message || 'Unable to delete manifest right now.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setManifestToDelete(null);
    }
  };

  // Handle download manifest
  const handleDownloadManifest = async (manifest: Manifest) => {
    try {
      const blob = await manifestService.downloadReport(manifest._id, 'excel');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manifest-${manifest.manifestNo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: 'Failed to download manifest report',
        description: error.message || 'Unable to download the manifest report.',
        variant: 'destructive',
      });
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFiltersChange = (nextFilters: typeof filters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedManifestId(null);
    setViewMode('list');
  };

  // Handle manifest update from detail view
  const handleManifestUpdate = async () => {
    // Refresh will be handled by detail component
  };

  // Render based on view mode
  const renderContent = () => {
    switch (viewMode) {
      case 'create':
        return (
          <CreateManifest
            onSuccess={handleCreateSuccess}
            onCancel={handleBackToList}
          />
        );

      case 'detail':
        return selectedManifestId ? (
          <ManifestDetail
            manifestId={selectedManifestId}
            onBack={handleBackToList}
            onUpdate={handleManifestUpdate}
          />
        ) : null;

      case 'list':
      default:
        return (
          <ManifestList
            manifests={manifests}
            loading={loading}
            total={total}
            page={page}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onPageChange={handlePageChange}
            onView={handleViewManifest}
            onLock={handleLockManifest}
            onDelete={handleDeleteManifest}
            onDownload={handleDownloadManifest}
          />
        );
    }
  };

  // Get selected manifest for header
  const selectedManifest = manifests.find(m => m._id === selectedManifestId);

  return (
    <div className='container mx-auto p-6 space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
          {viewMode !== 'list' && (
            <Button variant='outline' size='icon' onClick={handleBackToList}>
              <ArrowLeft className='h-4 w-4' />
            </Button>
          )}
          <div>
            <h1 className='text-2xl font-bold'>
              {viewMode === 'create'
                ? 'Create New Manifest'
                : viewMode === 'detail'
                  ? `Manifest: ${selectedManifest?.manifestNo || ''}`
                  : 'Manifest Management'}
            </h1>
            <p className='text-muted-foreground'>
              {viewMode === 'create'
                ? 'Create a new manifest for AWB shipments'
                : viewMode === 'detail'
                  ? 'View and manage manifest details, tracking, and billing'
                  : 'Manage manifests, track shipments, and handle billing'}
            </p>
          </div>
        </div>

        {viewMode === 'list' && (
          <Button className='w-full sm:w-auto' onClick={() => setViewMode('create')}>
            <Plus className='h-4 w-4 mr-2' />
            Create Manifest
          </Button>
        )}
      </div>

      <Dialog
        open={Boolean(manifestToDelete)}
        onOpenChange={open => {
          if (!open && !isDeleting) {
            setManifestToDelete(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete manifest?</DialogTitle>
            <DialogDescription>
              {manifestToDelete
                ? `This will permanently delete manifest ${manifestToDelete.manifestNo}. This action cannot be undone.`
                : 'This will permanently delete the selected manifest. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setManifestToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDeleteManifest}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Delete Manifest
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default ManifestManagement;
